import { useQuery } from '@tanstack/react-query';
import { post } from 'app/shared/utils/fetchr';
import { GetAIResultsPayload } from 'app/modules/aiAgentResults/requests';
import { AI_AGENT_RESULTS_QUERY_KEYS } from 'app/modules/aiAgentResults/queries/keys';
import { useSelector } from 'react-redux';
import {
  selectHasReadAlertsPermission,
  selectHasReadCasesPermissions,
  selectHasReadRulesPermission,
  selectHasReadSARsPermissions,
} from 'app/modules/session/selectors';
import { GetAIResultsResponse } from 'app/modules/aiAgentResults/responses';
import { useMemo } from 'react';

export const useGetAIResults = (
  payload: GetAIResultsPayload,
  options?: {
    enabled?: boolean;
    /** When true, disables `refetchInterval` entirely (no polling while `is_reviewing` or otherwise). */
    neverRetry?: boolean;
  },
) => {
  const hasReadAlertsPermission = useSelector(selectHasReadAlertsPermission);
  const hasReadCasesPermission = useSelector(selectHasReadCasesPermissions);
  const hasReadSARsPermission = useSelector(selectHasReadSARsPermissions);
  const hasReadRulesPermission = useSelector(selectHasReadRulesPermission);
  const hasPermission = useMemo(() => {
    switch (payload.relation_type) {
      case 'ALERT':
      case 'CUSTOM_TASK_PREVIEW':
        return hasReadAlertsPermission;
      case 'CASE':
        return hasReadCasesPermission;
      case 'FILING':
        return hasReadSARsPermission;
      case 'RULE':
        return hasReadRulesPermission;
      default:
        return false;
    }
  }, [
    hasReadAlertsPermission,
    hasReadCasesPermission,
    hasReadSARsPermission,
    hasReadRulesPermission,
    payload.relation_type,
  ]);
  const neverRetry = options?.neverRetry ?? false;

  return useQuery({
    enabled:
      (options?.enabled ?? true) &&
      hasPermission &&
      Number(payload.relation_id) > 0,
    queryFn: async () => {
      return post<GetAIResultsResponse>(
        '/ai-investigations/ai-agent-outputs',
        payload,
      );
    },
    queryKey: AI_AGENT_RESULTS_QUERY_KEYS.getAIResults(payload),
    refetchInterval: neverRetry
      ? false
      : (query) => {
          if (!query.state.data?.is_reviewing) {
            return false;
          }
          return 2_000;
        },
  });
};
